import { Field, Form } from 'react-final-form'
import { generatePath, useHistory } from 'react-router'
import { styleGuidePath } from 'app/navigation/paths'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import useGetIndustriesQuery from 'common/api/queries/useGetIndustriesQuery'
import useRegionsListQuery from 'common/api/queries/useRegionsListQuery'
import Button from 'common/components/button'
import CheckboxComponent from 'common/components/checkbox'
import ErrorPage from 'common/components/error/ErrorPage'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import Label from 'common/components/label'
import SingleSelect, { SelectValue } from 'common/components/singleSelect'
import Textarea from 'common/components/textarea'
import { errorToast } from 'common/components/toastNotification'
import Tooltip from 'common/components/Tooltip'
import Widget from 'common/components/Widget'
import WidgetHeader from 'common/components/WidgetHeader'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { BrandVoice } from 'common/interfaces/brandVoices'
import { selectBrandVoice } from 'features/admin/styleGuide/store/styleGuideSlice'

import { getFieldError, validateBrandVoiceName } from '../helpers'
import { createBrandVoice } from '../store/brandVoiceSlice'

type Props = {
  className?: string
  clonedBrandVoice?: BrandVoice
  onClose: () => void
}

const CreateWidget = ({ className, clonedBrandVoice, onClose }: Props) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { showBrandContextFields } = useFlags()

  const {
    editedContent: { brandVoices },
    accountBrandName,
    accountBrandDescription,
  } = useAppSelector((state) => state.brandVoice)
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const brandVoiceNames = brandVoices.map((brandVoice) => {
    return brandVoice.name
  })

  const { data: regionsData, status: regionStatus } = useRegionsListQuery()
  const regionOptions = regionsData?.map(({ id, name }) => ({
    value: id,
    label: name,
  }))

  const { data: industries } = useGetIndustriesQuery()
  const industryOptions = industries?.map((industry) => ({
    value: industry.name,
    label: industry.name,
  }))

  const validateName = (value: string | undefined) => {
    return !value
      ? 'Please enter the brand voice name.'
      : validateBrandVoiceName(value, brandVoiceNames)
  }

  const validateRegion = (value: SelectValue | undefined) => {
    return !value?.value ? 'Please select your region.' : undefined
  }
  const validateIndustry = (value: SelectValue | undefined) => {
    return !value?.value ? 'Please select your industry.' : undefined
  }

  const isFirstBrandVoice = brandVoices.length === 0

  if (regionStatus === 'error') {
    return <ErrorPage />
  }

  return (
    <div className="w-150 max-h-[85vh] overflow-y-auto mt-10">
      <Form
        initialValues={{
          name: clonedBrandVoice ? `${clonedBrandVoice.name} - COPY` : '',
          region: clonedBrandVoice
            ? {
                label: regionOptions?.find(
                  (region) =>
                    region.value.toLowerCase() ===
                    clonedBrandVoice.regionId.toLowerCase()
                )?.label,
                value: clonedBrandVoice.regionId,
              }
            : undefined,
          industry: clonedBrandVoice
            ? {
                label: clonedBrandVoice.industry,
                value: clonedBrandVoice.industry,
              }
            : undefined,
          isDefault: isFirstBrandVoice,
          brandName: '',
          brandDescription: '',
          inheritBrandContext: true,
        }}
        onSubmit={() => {}}
        render={({ submitting: isSubmitting, hasValidationErrors, values }) => (
          <Widget
            type="basic"
            className={cx(className)}
            bgClassName="bg-gold-50"
          >
            <WidgetHeader title="Create a new Brand Voice" />
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (hasValidationErrors) {
                  errorToast(
                    'Please complete all necessary fields before saving.'
                  )
                } else {
                  const brandVoice = {
                    name: values.name.trim(),
                    lastModified: '',
                    isDefault: values.isDefault ?? false,
                    regionId: values.region.value,
                    industry: values.industry?.value,
                    tones: [],
                    ...(showBrandContextFields && {
                      inheritBrandContext: values.inheritBrandContext ?? false,
                      brandName: values.inheritBrandContext
                        ? ''
                        : values.brandName || '',
                      brandDescription: values.inheritBrandContext
                        ? ''
                        : values.brandDescription || '',
                    }),
                  }
                  const result = await dispatch(
                    createBrandVoice({
                      brandVoice,
                      clonedBrandVoice,
                    })
                  ).unwrap()

                  const newBrandVoice = result.find(
                    (voice) => voice.name === brandVoice.name
                  )

                  onClose()

                  if (newBrandVoice?.id) {
                    dispatch(selectBrandVoice(newBrandVoice.id))

                    history.push(
                      generatePath(styleGuidePath, {
                        accountId,
                        brandVoiceId: newBrandVoice.id,
                      })
                    )
                  }
                }
              }}
            >
              <Field<string> name="name" validate={validateName}>
                {({ input, meta }) => (
                  <FormItem
                    label="Give your Brand Voice a name"
                    htmlFor="name"
                    error={getFieldError(meta)}
                  >
                    <Input
                      data-cy="brand-voice-name"
                      data-testid="brand-voice-name"
                      className="w-full mt-2"
                      type="text"
                      variant="default"
                      id="name"
                      name="name"
                      placeholder="Enter Brand Voice name"
                      value={input.value}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                    />
                  </FormItem>
                )}
              </Field>
              <Field<SelectValue> name="region" validate={validateRegion}>
                {({ input, meta }) => (
                  <FormItem
                    label="Region"
                    htmlFor="region"
                    error={getFieldError(meta)}
                  >
                    <div className="text-base-700">
                      Select a region below. This will set certain default
                      settings in your content generations.
                    </div>
                    <SingleSelect
                      className="w-full mt-2"
                      data-cy="region-select"
                      data-testid="region-select"
                      name="region"
                      placeholder="Select your region"
                      value={input.value.value}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      options={regionOptions}
                      menuPortalTarget={document.body}
                      isDisabled={!!clonedBrandVoice}
                    />
                  </FormItem>
                )}
              </Field>
              <Field<SelectValue>
                name="industry"
                validate={!clonedBrandVoice ? validateIndustry : undefined}
              >
                {({ input, meta }) => (
                  <FormItem
                    label="Brand Context"
                    htmlFor="industry"
                    error={getFieldError(meta)}
                  >
                    <div className="text-base-700">
                      This will add more context to the content you generate.
                    </div>
                    <Label className="mt-4" htmlFor="industry">
                      Industry
                    </Label>
                    <SingleSelect
                      className="w-full mt-2"
                      data-cy="industry-select"
                      data-testid="industry-select"
                      name="industry"
                      placeholder="Select your industry"
                      value={input.value.value}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      options={industryOptions}
                      menuPortalTarget={document.body}
                      isDisabled={!!clonedBrandVoice}
                    />
                  </FormItem>
                )}
              </Field>
              {showBrandContextFields && (
                <>
                  <Field<boolean> name="inheritBrandContext">
                    {({ input }) => (
                      <CheckboxComponent
                        className="mt-6 mb-4"
                        label="Use Account level Brand Name and Brand Description"
                        isChecked={input.value}
                        onChange={input.onChange}
                      />
                    )}
                  </Field>
                  <Field<string> name="brandName">
                    {({ input }) => (
                      <FormItem label="Brand name">
                        <Input
                          className="w-full mt-2"
                          type="text"
                          variant="default"
                          placeholder="Enter the company/brand name. This is used as context in content generation."
                          value={
                            values.inheritBrandContext
                              ? accountBrandName
                              : input.value
                          }
                          onChange={input.onChange}
                          onBlur={input.onBlur}
                          disabled={values.inheritBrandContext}
                        />
                      </FormItem>
                    )}
                  </Field>
                  <Field<string> name="brandDescription">
                    {({ input }) => (
                      <FormItem label="Brand description">
                        <Textarea
                          className="w-full mt-2"
                          placeholder="Provide a description of the business, 1-2 sentences. This is used as context in content generation."
                          value={
                            values.inheritBrandContext
                              ? accountBrandDescription
                              : input.value
                          }
                          rows={4}
                          onChange={input.onChange}
                          onBlur={input.onBlur}
                          disabled={values.inheritBrandContext}
                        />
                      </FormItem>
                    )}
                  </Field>
                </>
              )}
              <div className="border-t border-b border-gold-400 py-6 mt-6">
                <Field<boolean> name="isDefault">
                  {({ input }) => (
                    <div className="w-52">
                      <Tooltip
                        overlay="Default brand voice must be selected. Add more to change your default."
                        show={isFirstBrandVoice}
                      >
                        <CheckboxComponent
                          data-cy="default-checkbox"
                          data-testid="default-checkbox"
                          label="Mark as default"
                          className="mr-6"
                          isChecked={input.value || isFirstBrandVoice}
                          onChange={(e) => {
                            input.onChange(e)
                          }}
                          isDisabled={isFirstBrandVoice || !!clonedBrandVoice}
                        />
                      </Tooltip>
                    </div>
                  )}
                </Field>

                <div className="text-base-700 mt-2">
                  Your default voice will be automatically selected when
                  generating content. This should be the Brand Voice you use
                  most often.
                </div>
              </div>
              <div className="flex justify-end items-center mt-6">
                <Button
                  data-cy="generate-tone-button"
                  data-testid="generate-tone-cancel-button"
                  aria-label="Generate tone"
                  variant="link"
                  onClick={onClose}
                  className="text-base mr-4"
                >
                  Go Back
                </Button>
                <Button
                  type="submit"
                  data-cy="generate-tone-button"
                  data-testid="generate-tone-save-button"
                  aria-label="Generate tone"
                  variant="primary"
                  className="text-base"
                  disabled={isSubmitting || hasValidationErrors}
                >
                  Save
                </Button>
              </div>
            </form>
          </Widget>
        )}
      />
    </div>
  )
}

export default CreateWidget
